import { useEffect, useState, forwardRef } from "react";
import { Product, StickerConfiguration } from "@/types/stock";
import { generateBarcodeDataURL } from "@/utils/barcodeUtils";
import { defaultStickerConfig } from "@/config/appConfig";

interface BarcodeStickerProps {
  product: Product;
  barcode?: string;
  className?: string;
  config?: StickerConfiguration;
}

/**
 * BarcodeSticker component for generating printable product stickers
 * Contains product name, EAN13 barcode, and price in a configurable sticker format
 */
const BarcodeSticker = forwardRef<HTMLDivElement, BarcodeStickerProps>(
  (
    { product, barcode, className = "", config = defaultStickerConfig },
    ref
  ) => {
    const [barcodeDataURL, setBarcodeDataURL] = useState<string>("");
    const [currentBarcode, setCurrentBarcode] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");

    useEffect(() => {
      const generateBarcode = async () => {
        try {
          setLoading(true);
          setError("");

          // Determine which barcode to use
          const barcodeToUse = barcode || product.barcode;

          if (!barcodeToUse) {
            setError("No barcode available");
            setLoading(false);
            return;
          }

          // Set the current barcode
          setCurrentBarcode(barcodeToUse);

          // Generate barcode image using configuration
          const dataURL = await generateBarcodeDataURL(barcodeToUse, {
            format: "EAN13", // Try EAN13 first
            width: 1.5, // Thinner bars for small sticker
            height: config.barcodeHeight * 3.78, // Convert mm to px (approx)
            displayValue: true,
            fontSize: config.fontSize.other,
            textMargin: 2,
            margin: config.margin * 3.78, // Convert mm to px
            background: "#ffffff",
            lineColor: "#000000",
          });

          setBarcodeDataURL(dataURL);
        } catch (err) {
          console.error("Error generating barcode:", err);

          // If EAN13 fails, try CODE128 which is more flexible
          try {
            const barcodeToUse = barcode || product.barcode;
            if (barcodeToUse) {
              const dataURL = await generateBarcodeDataURL(barcodeToUse, {
                format: "CODE128",
                width: 1.5,
                height: config.barcodeHeight * 3.78, // Convert mm to px
                displayValue: true,
                fontSize: config.fontSize.other,
                textMargin: 2,
                margin: config.margin * 3.78, // Convert mm to px
                background: "#ffffff",
                lineColor: "#000000",
              });
              setBarcodeDataURL(dataURL);
            }
          } catch (fallbackErr) {
            console.error("Error generating fallback barcode:", fallbackErr);
            setError("Failed to generate barcode");
          }
        } finally {
          setLoading(false);
        }
      };

      generateBarcode();
    }, [product.id, product.barcode, barcode, config]);

    if (loading) {
      return (
        <div
          ref={ref}
          className={`bg-white border border-gray-300 p-4 rounded-lg ${className}`}
        >
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-16 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div
          ref={ref}
          className={`bg-white border border-red-300 p-4 rounded-lg ${className}`}
        >
          <div className="text-red-600 text-center">
            <p className="text-sm">{error}</p>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={`bg-white border border-gray-300 p-1 rounded-lg print:border-0 print:rounded-none print:shadow-none ${className}`}
        style={{
          width: `${config.dimensions.width}mm`,
          height: `${config.dimensions.height}mm`,
          fontSize: `${config.fontSize.other}px`,
          lineHeight: "1.1",
          padding: `${config.margin}mm`,
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Product Name */}
        {config.displayOptions.showProductName && (
          <div className="text-center mb-1">
            <h3
              className="font-bold text-black leading-tight"
              style={{
                fontSize: `${config.fontSize.productName}px`,
                maxHeight: `${config.fontSize.productName * 1.2}px`,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {product.name.length > 25
                ? product.name.substring(0, 25) + "..."
                : product.name}
            </h3>
          </div>
        )}

        {/* SKU */}
        {config.displayOptions.showSKU && (
          <div className="text-center mb-1">
            <span
              className="text-gray-600"
              style={{ fontSize: `${config.fontSize.sku}px` }}
            >
              {product.sku}
            </span>
          </div>
        )}

        {/* Category */}
        {config.displayOptions.showCategory && product.category && (
          <div className="text-center mb-1">
            <span
              className="text-gray-500"
              style={{ fontSize: `${config.fontSize.other}px` }}
            >
              {product.category.name}
            </span>
          </div>
        )}

        {/* Barcode */}
        {config.displayOptions.showBarcode && (
          <div className="flex justify-center mb-1">
            {barcodeDataURL ? (
              <img
                src={barcodeDataURL}
                alt={`Barcode: ${currentBarcode}`}
                className="max-w-full h-auto"
                style={{
                  maxHeight: `${config.barcodeHeight}mm`,
                  width: "auto",
                  maxWidth: `${config.dimensions.width - config.margin * 2}mm`,
                }}
              />
            ) : (
              <div className="h-6 bg-gray-200 rounded flex items-center justify-center text-xs">
                <span
                  className="text-gray-500"
                  style={{ fontSize: `${config.fontSize.other}px` }}
                >
                  No barcode
                </span>
              </div>
            )}
          </div>
        )}

        {/* Price */}
        {config.displayOptions.showPrice && (
          <div className="text-center">
            <div
              className="font-bold text-green-600"
              style={{ fontSize: `${config.fontSize.price}px` }}
            >
              {product.price.toFixed(3)} TND
            </div>
          </div>
        )}
      </div>
    );
  }
);

BarcodeSticker.displayName = "BarcodeSticker";

export default BarcodeSticker;
